import React, { useState } from 'react';
import { useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Globe, Linkedin, Phone, Smartphone, Mail, CheckCircle, X, Users, Target, AlertCircle, Loader2, ArrowLeft, Building2, UserCheck, Shield, BadgeCheck } from 'lucide-react';
import PaginationBar from '../shared/PaginationBar';
import { usePreQualifiedLeads } from '../../hooks/useSupabaseLeads';
import StatsCard from '../Dashboard/StatsCard';
import FeedbackPopup from '../FeedbackPopup';
import SimpleCustomSelect from '../shared/SimpleCustomSelect';
import LoadingScreen from '../shared/LoadingScreen';

const PreQualifiedLeadsStandalonePage: React.FC = () => {
  const navigate = useNavigate();
  const { data: preQualifiedLeads, isLoading, error } = usePreQualifiedLeads();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDisqualificationModal, setShowDisqualificationModal] = useState(false);
  const [selectedDisqualifiedLead, setSelectedDisqualifiedLead] = useState<any>(null);
  const [isPushingToQualified, setIsPushingToQualified] = useState(false);
  const itemsPerPage = 10;

  const getDerivedStatus = (lead: any): string => {
    const raw = (lead.lead_status || '').toLowerCase();
    if (raw.includes('ready to go')) return 'ready to go';
    if (raw.includes('looks good')) return 'looks good';
    if (raw.includes('reach via linkedin')) return 'reach via linkedin';
    if (raw.includes('dm disqualified')) return 'dm disqualified';
    if (raw.includes('disqualified')) return 'company disqualified';
    if (lead.company_qualified === false && (lead.cp_disq_reason || lead.lead_status?.toLowerCase().includes('disqualified'))) return 'company disqualified';
    if (lead.company_qualified === true && lead.dm_qualified === false && lead.dm_disq_reason) return 'dm disqualified';
    if (raw.includes('needs enrichment') || raw === '') return 'needs enrichment';
    return raw || 'in review';
  };

  const filteredLeads = preQualifiedLeads.filter(lead => {
    const matchesSearch = (lead.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead.dm_full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead.dm_email_1 || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead.industry_1 || '').toLowerCase().includes(searchTerm.toLowerCase());
    const derived = getDerivedStatus(lead);
    const matchesStatus = statusFilter === 'all' || derived === statusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;

  const paginatedLeads = useMemo(() => {
    const slicedData = filteredLeads.slice(startIndex, startIndex + itemsPerPage);
    return slicedData;
  }, [currentPage, filteredLeads, itemsPerPage, startIndex]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const handleDisqualifiedClick = (lead: any) => {
    setSelectedDisqualifiedLead(lead);
    setShowDisqualificationModal(true);
  };

  const handleCloseDisqualificationModal = () => {
    setShowDisqualificationModal(false);
    setSelectedDisqualifiedLead(null);
    setIsPushingToQualified(false);
  };

  const handlePushToQualified = async () => {
    if (!selectedDisqualifiedLead) return;

    setIsPushingToQualified(true);

    try {
      handleCloseDisqualificationModal();

    } catch (error) {
      console.error('[PreQualifiedLeadsStandalonePage] Error in handlePushToQualified:', error);
    } finally {
      setIsPushingToQualified(false);
    }
  };

  const totalPreQualifiedLeads = preQualifiedLeads.length;
  const totalQualifiedLeads = preQualifiedLeads.filter(lead => {
    const status = lead.lead_status?.toLowerCase();
    return status?.includes('ready to go') ||
           status?.includes('reach via linkedin') ||
           status?.includes('looks good');
  }).length;
  const totalDisqualifiedLeads = preQualifiedLeads.filter(lead => {
    const derived = getDerivedStatus(lead);
    return derived === 'company disqualified' || derived === 'dm disqualified';
  }).length;
  const percentageQualifiedLeads = totalPreQualifiedLeads > 0 ?
    Math.round((totalQualifiedLeads / totalPreQualifiedLeads) * 100) : 0;

  if (isLoading && preQualifiedLeads.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/ai-agents/lead-gen')}
          className="flex items-center text-gray-600 hover:text-[#1875FF] transition-colors font-medium"
        >
          <ArrowLeft size={20} className="mr-1" />
          Back to Lead Gen Agent
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-800 p-4 rounded-xl flex items-center space-x-2 shadow-sm">
          <AlertCircle size={20} />
          <span className="font-medium">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Pre-Qualified Leads"
          value={totalPreQualifiedLeads}
          icon={Users}
          color="blue"
        />
        <StatsCard
          title="Total Qualified Leads"
          value={totalQualifiedLeads}
          icon={CheckCircle}
          color="green"
        />
        <StatsCard
          title="Total Disqualified Leads"
          value={totalDisqualifiedLeads}
          icon={X}
          color="red"
        />
        <StatsCard
          title="% of Qualified Leads"
          value={`${percentageQualifiedLeads}%`}
          icon={Target}
          color="purple"
        />
      </div>

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-5 shadow-lg">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
              <input
                type="text"
                placeholder="Search pre-qualified leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1875FF] focus:border-[#1875FF] transition-all font-medium"
              />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter size={16} className="text-gray-500" />
              <SimpleCustomSelect
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'ready to go', label: 'Ready To Go' },
                  { value: 'reach via linkedin', label: 'Reach via LinkedIn' },
                  { value: 'looks good', label: 'Looks Good' },
                  { value: 'needs enrichment', label: 'Needs Enrichment' },
                  { value: 'company disqualified', label: 'Company Disqualified' },
                  { value: 'dm disqualified', label: 'DM Disqualified' }
                ]}
                placeholder="All Status"
                className="w-56"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 shadow-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-max">
            <thead className="bg-gradient-to-r from-[#05E0D1]/10 to-[#1875FF]/10 border-b-2 border-[#1875FF]/10">
              <tr>
                <th className="text-left px-4 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-48">
                  Company
                </th>
                <th className="text-left px-4 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-48">
                  Industry & Products
                </th>
                <th className="text-left px-4 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-44">
                  Person
                </th>
                <th className="text-left px-4 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-48">
                  Contact Details
                </th>
                <th className="text-left px-3 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-36">
                  Qualification & Verification
                </th>
                <th className="text-center px-3 py-4 text-xs font-bold text-gray-700 uppercase tracking-widest font-sans w-32">
                  Lead Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1875FF]/10">
              {paginatedLeads.map((lead) => (
                <tr key={lead.lead_id} className="hover:bg-[#1875FF]/5 transition-colors">
                  <td className="px-4 py-4">
                    <div className="font-medium text-gray-900 mb-2">{lead.company_name || 'N/A'}</div>
                    <div className="flex items-center space-x-2">
                      {lead.company_website && (
                        <a
                          href={lead.company_website.startsWith('http') ? lead.company_website : `https://${lead.company_website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="Visit Website"
                        >
                          <Globe size={16} />
                        </a>
                      )}
                      {lead.company_linkedin && (
                        <a
                          href={lead.company_linkedin.startsWith('http') ? lead.company_linkedin : `https://${lead.company_linkedin}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="LinkedIn Profile"
                        >
                          <Linkedin size={16} />
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900 break-words">
                      {lead.industry_1 && lead.industry_1.trim() ? (
                        lead.industry_1
                      ) : (
                        <span className="text-gray-400 italic flex items-center space-x-1">
                          <Loader2 size={12} className="animate-spin" />
                          <span>Analyzing...</span>
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 mt-1 break-words">
                      {lead.products_services && lead.products_services.trim() ? (
                        lead.products_services
                      ) : (
                        <span className="text-gray-400 italic flex items-center space-x-1">
                          <Loader2 size={12} className="animate-spin" />
                          <span>Analyzing...</span>
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center space-x-2">
                      <div className="font-medium text-gray-900">
                        {lead.dm_full_name && lead.dm_full_name.trim() ? (
                          lead.dm_full_name
                        ) : (
                          <span className="text-gray-400 italic flex items-center space-x-1">
                            <Search size={12} className="animate-pulse" />
                            <span>Searching...</span>
                          </span>
                        )}
                      </div>
                      {lead.dm_linkedin && (
                        <a
                          href={lead.dm_linkedin.startsWith('http') ? lead.dm_linkedin : `https://${lead.dm_linkedin}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#1875FF] hover:text-[#05E0D1] transition-colors"
                          title="LinkedIn Profile"
                        >
                          <Linkedin size={16} />
                        </a>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 break-words mt-1">
                      {lead.dm_title && lead.dm_title.trim() ? (
                        lead.dm_title
                      ) : (
                        lead.dm_full_name && lead.dm_full_name.trim() ? (
                          <span className="text-gray-400 italic flex items-center space-x-1">
                            <Search size={12} className="animate-pulse" />
                            <span>Searching...</span>
                          </span>
                        ) : null
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="space-y-2">
                      {lead.company_phone && (
                        <div className="flex items-center space-x-2 text-sm text-gray-900">
                          <Phone size={14} className="text-[#1875FF]" />
                          <span>{lead.company_phone}</span>
                        </div>
                      )}
                      {!lead.company_phone && (
                        <div className="flex items-center space-x-2 text-sm text-gray-400 italic">
                          <Phone size={14} className="text-gray-400" />
                          <Search size={12} className="animate-pulse" />
                          <span>Searching...</span>
                        </div>
                      )}
                      {lead.dm_mobile_1 && (
                        <div className="flex items-center space-x-2 text-sm text-gray-900">
                          <Smartphone size={14} className="text-[#05E0D1]" />
                          <span>{lead.dm_mobile_1}</span>
                        </div>
                      )}
                      {!lead.dm_mobile_1 && (
                        <div className="flex items-center space-x-2 text-sm text-gray-400 italic">
                          <Smartphone size={14} className="text-gray-400" />
                          <Search size={12} className="animate-pulse" />
                          <span>Searching...</span>
                        </div>
                      )}
                      {lead.dm_email_1 && (
                        <div className="flex items-center space-x-2 text-sm text-gray-900">
                          <Mail size={14} className="text-[#1875FF]" />
                          <span>{lead.dm_email_1}</span>
                        </div>
                      )}
                      {!lead.dm_email_1 && (
                        <div className="flex items-center space-x-2 text-sm text-gray-400 italic">
                          <Mail size={14} className="text-gray-400" />
                          <Search size={12} className="animate-pulse" />
                          <span>Searching...</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="space-y-1.5">
                      <div className="flex items-center space-x-2 text-sm">
                        <Building2 size={14} className="text-gray-600" />
                        <span className="text-gray-700 w-16 text-xs font-medium">Company</span>
                        {lead.company_qualified ? (
                          <div className="relative">
                            <CheckCircle size={16} className="text-[#05E0D1] drop-shadow-[0_0_6px_rgba(5,224,209,0.5)]" />
                          </div>
                        ) : (
                          <X size={16} className="text-red-500" />
                        )}
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <UserCheck size={14} className="text-gray-600" />
                        <span className="text-gray-700 w-16 text-xs font-medium">Person</span>
                        {lead.dm_qualified ? (
                          <div className="relative">
                            <CheckCircle size={16} className="text-[#05E0D1] drop-shadow-[0_0_6px_rgba(5,224,209,0.5)]" />
                          </div>
                        ) : (
                          <X size={16} className="text-red-500" />
                        )}
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <Smartphone size={14} className="text-gray-600" />
                        <span className="text-gray-700 w-16 text-xs font-medium">Mobile</span>
                        {lead.mobile_verified ? (
                          <div className="relative">
                            <BadgeCheck size={16} className="text-[#1875FF] drop-shadow-[0_0_6px_rgba(24,117,255,0.5)]" />
                          </div>
                        ) : (
                          <X size={16} className="text-red-500" />
                        )}
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <Mail size={14} className="text-gray-600" />
                        <span className="text-gray-700 w-16 text-xs font-medium">Email</span>
                        {lead.email_verified ? (
                          <div className="relative">
                            <BadgeCheck size={16} className="text-[#1875FF] drop-shadow-[0_0_6px_rgba(24,117,255,0.5)]" />
                          </div>
                        ) : (
                          <X size={16} className="text-red-500" />
                        )}
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <Linkedin size={14} className="text-gray-600" />
                        <span className="text-gray-700 w-16 text-xs font-medium">LinkedIn</span>
                        {lead.linkedin_verified ? (
                          <div className="relative">
                            <BadgeCheck size={16} className="text-[#1875FF] drop-shadow-[0_0_6px_rgba(24,117,255,0.5)]" />
                          </div>
                        ) : (
                          <X size={16} className="text-red-500" />
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-center">
                    {(() => {
                      const derived = getDerivedStatus(lead);

                      switch (derived) {
                        case 'company disqualified':
                          return (
                            <button
                              onClick={() => handleDisqualifiedClick(lead)}
                              className="inline-flex items-center whitespace-nowrap px-2.5 py-1.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-red-500/20 to-red-600/20 text-red-600 border-2 border-red-500/40 hover:border-red-500/60 hover:shadow-[0_0_12px_rgba(239,68,68,0.4)] transition-all cursor-pointer"
                            >
                              <X size={10} className="mr-0.5 shrink-0" />
                              Company Disqualified
                            </button>
                          );
                        case 'dm disqualified':
                          return (
                            <button
                              onClick={() => handleDisqualifiedClick(lead)}
                              className="inline-flex items-center whitespace-nowrap px-2.5 py-1.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-red-500/20 to-red-600/20 text-red-600 border-2 border-red-500/40 hover:border-red-500/60 hover:shadow-[0_0_12px_rgba(239,68,68,0.4)] transition-all cursor-pointer"
                            >
                              <X size={10} className="mr-0.5 shrink-0" />
                              DM Disqualified
                            </button>
                          );
                        case 'ready to go':
                          return (
                            <span className="inline-flex items-center whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-[#05E0D1]/20 to-[#05E0D1]/30 text-[#05E0D1] border-2 border-[#05E0D1]/50 shadow-[0_0_12px_rgba(5,224,209,0.3)]">
                              <CheckCircle size={12} className="mr-1 shrink-0" />
                              Ready to Go
                            </span>
                          );
                        case 'looks good':
                          return (
                            <span className="inline-flex items-center whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-green-400/20 to-green-500/30 text-green-600 border-2 border-green-500/50 shadow-[0_0_12px_rgba(34,197,94,0.3)]">
                              <CheckCircle size={12} className="mr-1 shrink-0" />
                              Looks Good
                            </span>
                          );
                        case 'reach via linkedin':
                          return (
                            <span className="inline-flex items-center whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-[#1875FF]/20 to-[#1875FF]/30 text-[#1875FF] border-2 border-[#1875FF]/50 shadow-[0_0_12px_rgba(24,117,255,0.3)]">
                              <Linkedin size={12} className="mr-1 shrink-0" />
                              Reach via LinkedIn
                            </span>
                          );
                        case 'needs enrichment':
                          return (
                            <span className="inline-flex items-center whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 border-2 border-gray-300">
                              Needs Enrichment
                            </span>
                          );
                        default:
                          return (
                            <span className="inline-flex items-center whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 border-2 border-gray-300">
                              In Review
                            </span>
                          );
                      }
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredLeads.length === 0 && !isLoading && (
        <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-12 text-center shadow-xl">
          <div className="text-gray-700 text-lg font-extrabold font-display">No pre-qualified leads found</div>
          <div className="text-gray-600 text-sm mt-1 font-medium">
            {searchTerm || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Pre-qualified leads will appear here once validation is complete'}
          </div>
        </div>
      )}

      <PaginationBar
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalItems={filteredLeads.length}
        startIndex={startIndex}
        itemsPerPage={itemsPerPage}
      />

      {showDisqualificationModal && selectedDisqualifiedLead && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4">
            <div className="bg-gradient-to-r from-[#05E0D1] to-[#1875FF] p-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-xl font-extrabold text-white font-display">Disqualification Reason</h2>
              <button
                onClick={handleCloseDisqualificationModal}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-5">
              {selectedDisqualifiedLead.cp_disq_reason && (
                <div className="mb-3">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Company Disqualification</div>
                  <div className="text-sm text-gray-900 bg-gradient-to-br from-white to-[#1875FF]/5 border-2 border-[#1875FF]/20 p-4 rounded-xl font-medium">
                    {selectedDisqualifiedLead.cp_disq_reason}
                  </div>
                </div>
              )}
              {selectedDisqualifiedLead.dm_disq_reason && (
                <div className="mb-3">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Person Disqualification</div>
                  <div className="text-sm text-gray-900 bg-gradient-to-br from-white to-[#1875FF]/5 border-2 border-[#1875FF]/20 p-4 rounded-xl font-medium">
                    {selectedDisqualifiedLead.dm_disq_reason}
                  </div>
                </div>
              )}
              {!selectedDisqualifiedLead.cp_disq_reason && !selectedDisqualifiedLead.dm_disq_reason && (
                <div className="text-sm text-gray-900 bg-gradient-to-br from-white to-[#1875FF]/5 border-2 border-[#1875FF]/20 p-4 rounded-xl mb-3 font-medium">
                  No reason provided
                </div>
              )}

              <button
                onClick={handlePushToQualified}
                disabled={isPushingToQualified}
                className="w-full bg-gradient-to-r from-[#05E0D1] to-[#1875FF] text-white py-3 px-4 rounded-xl font-semibold hover:shadow-lg hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center space-x-2"
              >
                {isPushingToQualified ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <CheckCircle size={20} />
                )}
                <span>{isPushingToQualified ? 'Pushing...' : 'Push To Qualified'}</span>
              </button>
              </div>
            </div>
        </div>,
        document.body
      )}

      <FeedbackPopup isVisible={true} />
    </div>
  );
};

export default PreQualifiedLeadsStandalonePage;
